import { ValidationError } from "sequelize"
import {
    LifecyclePhase,
    LifecyclePhaseCreate,
    LifecyclePhaseSave,
    LifecyclePhaseSavePosition,
} from "server/models"
import { GET, POST, DELETE, validate } from "client/store/utils"
import {
    LangType,
    LifecyclePhaseValidation,
    NestedPhase,
    ImportLifecyclePhasesType,
    LoadAllLifecycleConnections,
} from "client/store"


export const LoadAllLifecyclePhases = (lifecycleId: number) => (
    GET(`/lifecyclePhase/all/lifecycle/${lifecycleId}`)
    .then((phaseList: LifecyclePhase[]) => phaseList)
)

export const LoadLifecyclePhases = (lifecycleId: number, phaseId: number) => (
    GET(`/lifecyclePhase/lifecycle/${lifecycleId}/phase/${phaseId}`)
    .then((phases: LifecyclePhase[]) => {
        const phaseList: LifecyclePhase[] = []

        phases.forEach((phase) => {
            phaseList.push({
                ...phase,
                positionObject: phase.position ? JSON.parse(phase.position) : null,
            })
        })

        return phaseList
    })
)

// get number of sub-levels
export const LoadDistinctLifecyclePhaseParents = (lifecycleId: number) => (
    GET(`/lifecyclePhase/distinct/lifecycle/${lifecycleId}`)
    .then((distinctIds: number[]) => distinctIds)
)

export const AddLifecyclePhase = (
    phase: LifecyclePhaseCreate,
    maxSubLifecycles: number,
    lang: LangType,
) => (
    LoadDistinctLifecyclePhaseParents(phase.lifecycleId)
    .then((distinct) => {
        let allowedToAdd = false
        if (phase.phaseId === null || distinct.indexOf(phase.phaseId) >= 0) allowedToAdd = true // add to existing level
        else if (distinct.length < maxSubLifecycles || maxSubLifecycles === null) allowedToAdd = true // add to new level

        if (allowedToAdd) {
            return POST(
                "/lifecyclePhase/add",
                JSON.stringify(phase),
            ).then((res) => {
                const newPhase = res as LifecyclePhase
                const error = res as ValidationError

                return {
                    newPhaseId: newPhase.id || null,
                    validation: <LifecyclePhaseValidation>validate(error, lang),
                }
            })
        }

        return {
            newPhaseId: null,
            validation: <LifecyclePhaseValidation>{
                name: lang.maxSubLifecyclesReached.replace("{0}", maxSubLifecycles.toString()),
            },
        }
    })
)

export const UpdateLifecyclePhase = (
    id: number,
    phase: LifecyclePhaseCreate,
    lang: LangType,
) => {
    const data: LifecyclePhaseSave = {
        id,
        phase,
    }
    return POST(
        "/lifecyclePhase/update",
        JSON.stringify(data),
    ).then((res) => {
        const error = res as ValidationError
        return {
            validation: <LifecyclePhaseValidation>validate(error, lang),
        }
    })
}

export const UpdateLifecyclePhasePosition = (
    id: number,
    position: { x: number, y: number },
) => {
    const data: LifecyclePhaseSavePosition = {
        id,
        position: JSON.stringify(position),
    }
    return POST(
        "/lifecyclePhase/updatePosition",
        JSON.stringify(data),
    )
}

export const DeleteLifecyclePhase = (phaseId: number) => (
    DELETE(`/lifecyclePhase/delete/${phaseId}`)
    .then((removedRows: number) => removedRows)
)

export const LoadNestedLifecyclePhases = (lifecycleId: number) => (
    LoadAllLifecyclePhases(lifecycleId)
    .then((phases) => (
        LoadAllLifecycleConnections(lifecycleId)
        .then((connections) => {
            const phasesNested: { [phaseId: number]: NestedPhase } = {}

            phasesNested[0] = {
                phase: null,
                phases: [],
                connections: [],
            }
            phases.forEach((phase) => {
                phasesNested[phase.id] = {
                    phase,
                    phases: [],
                    connections: [],
                }
            })

            // prepare connections
            connections.forEach((connection) => {
                const parentPhaseId = connection.phaseId === null ? 0 : connection.phaseId
                const parent = phasesNested[parentPhaseId]
                if (parent) {
                    const newConnections = parent.connections.slice()
                    newConnections.push(connection)
                    parent.connections = newConnections
                }
            })

            // prepare nested phases
            phases.forEach((phase) => {
                const parentId = phase.phaseId === null ? 0 : phase.phaseId
                const parent = phasesNested[parentId]
                const current = phasesNested[phase.id]
                if (parent && current) {
                    const newPhases = parent.phases.slice()
                    newPhases.push(current)
                    parent.phases = newPhases

                    delete phasesNested[phase.id] // delete current
                }
            })

            return phasesNested[0]
        })
    ))
)

export const ImportLifecyclePhases = (
    nestedPhase: NestedPhase,
    newLifecycleId: number,
) => {
    const data: ImportLifecyclePhasesType = {
        nestedPhase,
        newLifecycleId,
    }
    return POST(
        "/lifecyclePhase/import",
        JSON.stringify(data),
    )
}
